/**
 * Histopathology Reporting – Pending List.
 * Ported from admin4.0 HistoPendingReport.js.
 * GET /histopathology/result-page/ with optional ?date=YYYY-MM-DD
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateTime } from "luxon";
import { CommonGridToolBarWithFilterText } from "../../Components/Common Components/CommonComponents";
import CommonValidation from "../../Utility/CommonValidation";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import { ImagePaths } from "../../Utility/ImagePaths";
import { Colors } from "../../Styles/Colors";
import { CurrencySymbol } from "../../Utility/Constants";
import ResultEnterPopup from "./ResultEnterPopup";
import { canEditTab } from "../../Utility/Services/RoleAccessService";
import "../../Styles/Configuration.css";

function PatientDetailsCell({ data }) {
  if (!data) return null;
  const name = CommonValidation.removeSpace(data?.name ?? data?.patient_name) || "-";
  const sub = [data?.nxt_id ?? "-", data?.patients_mobile ?? data?.mobile_no].filter(Boolean).join(" | ");
  return (
    <Box sx={{ lineHeight: 1.3 }}>
      <Typography variant="body2" fontWeight={600} sx={{ display: "block", margin: 0 }}>{name}</Typography>
      {sub ? <Typography variant="caption" color="text.secondary" sx={{ display: "block", margin: 0, marginTop: "2px" }}>{sub}</Typography> : null}
    </Box>
  );
}

const statusOptions = [
  { id: 1, label: "Pending", value: "pending" },
  { id: 2, label: "Accept", value: "accept" },
  { id: 3, label: "Completed", value: "completed" },
];

export default function PendingList() {
  const [states, setState] = useState({
    pendingList: [],
    page: 0,
    pageSize: 10,
    fromDate: DateTime.now().toFormat("yyyy-MM-dd"),
    dateIndex: 1,
    loading: false,
    isErrorMsg: false,
    isErrorMsgText: "",
    isSuccessMsg: false,
    isSuccessMsgText: "",
    statusFilter: "all",
    signatureData: [],
    templateData: [],
    resultPopupData: null,
    resultPopupLoading: false,
    templatePopUpOpen: false,
    selectedPatient: null,
  });

  useEffect(() => {
    RestAPIService.getAll(Serviceurls.HISTOPATHOLOGY_ESIGN_CONFIG)
      .then((res) => {
        if (res?.data?.status === "success") {
          const list = res.data.data?.filter((item) => item.status) || [];
          setState((prev) => ({ ...prev, signatureData: list }));
        }
      })
      .catch((err) => setState((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: err?.response?.data?.message || err?.message })));
  }, []);

  useEffect(() => {
    if (!states.templatePopUpOpen || !states.selectedPatient?.bill_id) {
      if (!states.templatePopUpOpen) setState((prev) => ({ ...prev, templateData: [] }));
      return;
    }
    const billId = states.selectedPatient.bill_id;
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_TEMPLATE}?bill_id=${billId}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setState((prev) => ({ ...prev, templateData: res.data.data ?? [] }));
        } else {
          setState((prev) => ({ ...prev, templateData: [] }));
        }
      })
      .catch(() => setState((prev) => ({ ...prev, templateData: [] })));
  }, [states.templatePopUpOpen, states.selectedPatient?.bill_id]);

  useEffect(() => {
    if (!states.templatePopUpOpen || !states.selectedPatient) {
      setState((prev) => ({ ...prev, resultPopupData: null, resultPopupLoading: false }));
      return;
    }
    const resultDetailId = states.selectedPatient.action_id ?? states.selectedPatient.result_detail_id;
    if (!resultDetailId) {
      setState((prev) => ({ ...prev, resultPopupData: null, resultPopupLoading: false }));
      return;
    }
    setState((prev) => ({ ...prev, resultPopupLoading: true }));
    const url = `${Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_POPUP}?result_detail_id=${resultDetailId}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success" && res?.data?.data) {
          setState((prev) => ({ ...prev, resultPopupData: res.data.data, resultPopupLoading: false }));
        } else {
          setState((prev) => ({ ...prev, resultPopupData: null, resultPopupLoading: false }));
        }
      })
      .catch((err) => {
        setState((prev) => ({
          ...prev,
          resultPopupData: null,
          resultPopupLoading: false,
          isErrorMsg: true,
          isErrorMsgText: err?.response?.data?.message ?? err?.message ?? "Failed to load popup data.",
        }));
      });
  }, [states.templatePopUpOpen, states.selectedPatient?.action_id, states.selectedPatient?.result_detail_id]);

  const fetchResultPageList = useCallback(() => {
    setState((prev) => ({ ...prev, loading: true }));
    const date = states.fromDate || DateTime.now().toFormat("yyyy-MM-dd");
    const params = new URLSearchParams();
    if (date) params.set("date", date);
    const query = params.toString();
    const url = query ? `${Serviceurls.HISTOPATHOLOGY_RESULT_PAGE}?${query}` : Serviceurls.HISTOPATHOLOGY_RESULT_PAGE;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          const list = res.data.data || [];
          const mapped = list.map((item) => ({
            ...item,
            name: item.patient_name ?? item.name,
            patients_mobile: item.mobile_no ?? item.patients_mobile,
            patient_account_number: item.patient_identifier ?? item.patient_account_number,
            net_amount: item.bill_amount ?? item.net_amount,
            balance_amount: item.balance ?? item.balance_amount,
            test_name: item.test_names ?? item.test_name,
          }));
          setState((prev) => ({ ...prev, pendingList: mapped, loading: false }));
        } else {
          setState((prev) => ({ ...prev, pendingList: [], loading: false }));
        }
      })
      .catch((err) => {
        setState((prev) => ({
          ...prev,
          pendingList: [],
          loading: false,
          isErrorMsg: true,
          isErrorMsgText: err?.response?.data?.message || err?.message || "Failed to load result page list",
        }));
      });
  }, [states.fromDate]);

  useEffect(() => {
    fetchResultPageList();
  }, [fetchResultPageList]);

  const errorMessage = (msg) => setState((prev) => ({ ...prev, isErrorMsg: true, isErrorMsgText: msg }));
  const successMessage = (msg) => setState((prev) => ({ ...prev, isSuccessMsg: true, isSuccessMsgText: msg }));
  const msgClose = () => setState((prev) => ({ ...prev, isErrorMsg: false, isErrorMsgText: "", isSuccessMsg: false, isSuccessMsgText: "" }));

  const postResultTemplateData = (resultHtml, selectedLabIncharge, selectedPathologist2, options = {}) => {
    if (!options.isDispatch && !resultHtml?.replace(/<[^>]*>/g, "").trim()) {
      errorMessage("Please enter Result content.");
      return;
    }
    const row = states.selectedPatient;
    const resultDetailId = row?.action_id ?? row?.result_detail_id;
    if (!resultDetailId && !options.isDispatch) {
      errorMessage("Invalid row. Please try again.");
      return;
    }

    if (options.isDispatch) {
      // TODO: dispatch API if different from save
      successMessage("Dispatched.");
      setState((prev) => ({ ...prev, templatePopUpOpen: false, selectedPatient: null, resultPopupData: null }));
      fetchResultPageList();
      return;
    }

    const payload = {
      result_detail_id: resultDetailId,
      status: options.isForCompleted ? "Submitting for Approval" : "save",
      report_content: resultHtml?.trim() ?? "",
      lab_incharge_id: selectedLabIncharge?.id ?? null,
      pathologist_1_id: selectedPathologist2?.id ?? null,
    };

    setState((prev) => ({ ...prev, loading: true }));
    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_RESULT_PAGE_SAVE)
      .then((res) => {
        if (res?.data?.status === "success") {
          successMessage(res?.data?.message ?? (options.isForCompleted ? "Result sent for approval." : "Result saved."));
          setState((prev) => ({
            ...prev,
            loading: false,
            templatePopUpOpen: false,
            selectedPatient: null,
            resultPopupData: null,
          }));
          fetchResultPageList();
        } else {
          setState((prev) => ({ ...prev, loading: false }));
          errorMessage(res?.data?.message ?? "Save failed.");
        }
      })
      .catch((err) => {
        setState((prev) => ({ ...prev, loading: false }));
        errorMessage(err?.response?.data?.message ?? err?.message ?? "Failed to save result.");
      });
  };

  const rows = useMemo(() => {
    const list = states.pendingList || [];
    const statusFilter = states.statusFilter || "all";
    const filtered = list.filter((el) => {
      if (statusFilter === "all") return true;
      const s = (el?.status || "").toLowerCase();
      return s === statusFilter;
    });
    return filtered.map((el, i) => ({
      ...el,
      sno: i + 1,
      patientdetils: `${CommonValidation.removeSpace(el?.name) || ""} ${el?.patients_mobile || ""} ${el?.patient_account_number || ""}`,
    }));
  }, [states.pendingList, states.statusFilter]);

  const columns = useMemo(
    () => [
      {
        field: "patientdetils",
        headerName: "Patient Details",
        flex: 0.2,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box>
            <PatientDetailsCell data={params?.row} />
          </Box>
        ),
      },
      {
        field: "net_amount",
        headerName: "Payment Status",
        flex: 0.15,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box mt={"0.5vw"}>
            <Typography variant="body2" sx={{ fontSize: "0.85dvw" }}>
              Bill : {CurrencySymbol} {params.row.net_amount}
            </Typography>
            <Typography variant="body2" color="error" sx={{ fontSize: "0.85dvw" }}>
              Balance : {CurrencySymbol} {params.row.balance_amount}
            </Typography>
          </Box>
        ),
      },
      {
        field: "test_name",
        headerName: "Test Name",
        flex: 0.2,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.9dvw" }}>
            {params?.row?.test_name?.length > 30 ? (
              <Tooltip title={params.row.test_name} placement="top" arrow>
                <Box component="span">{params.row.test_name.slice(0, 30) + "..."}</Box>
              </Tooltip>
            ) : (
              <Box>{params?.row?.test_name || "-"}</Box>
            )}
          </Box>
        ),
      },
      {
        field: "specimen",
        headerName: "Specimen",
        flex: 0.12,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.9dvw" }}>{params?.row?.specimen ?? "-"}</Box>
        ),
      },
      {
        field: "sample_id",
        headerName: "Sample ID",
        flex: 0.11,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.9dvw" }}>{params?.row?.sample_id ?? "-"}</Box>
        ),
      },
      {
        field: "overall_sample_id",
        headerName: "Overall Sample ID",
        flex: 0.12,
        headerAlign: "left",
        align: "left",
        renderCell: (params) => (
          <Box sx={{ fontSize: "0.9dvw" }}>{params?.row?.overall_sample_id ?? "-"}</Box>
        ),
      },
      {
        field: "action",
        headerName: "Actions",
        headerAlign: "left",
        align: "left",
        flex: 0.1,
        sortable: false,
        renderCell: (params) => (
          <IconButton
            size="small"
            sx={{ minWidth: 40 }}
            onClick={() =>
              setState((prev) => ({
                ...prev,
                templatePopUpOpen: true,
                selectedPatient: params?.row,
              }))
            }
          >
            <Box component="img" sx={{ width: "1.5dvw", height: "1.5dvw" }} src={ImagePaths.Result?.default ?? ImagePaths.Result} alt="result" />
          </IconButton>
        ),
      },
    ],
    []
  );

  const handleStatusFilterChange = (event) => {
    setState((prev) => ({ ...prev, statusFilter: event.target.value }));
  };

  const FilterBar = () => (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 2,
        p: 1.5,
        backgroundColor: "#fafafa",
        borderBottom: "1px solid #e0e0e0",
        minHeight: "8.5vh",
      }}
    >
      <TextField
        size="small"
        type="date"
        label="Date"
        value={states.fromDate}
        onChange={(e) => setState((prev) => ({ ...prev, fromDate: e.target.value || DateTime.now().toFormat("yyyy-MM-dd") }))}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
        inputProps={{ "aria-label": "Select date" }}
      />
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id="pending-status-filter">Status</InputLabel>
        <Select
          labelId="pending-status-filter"
          label="Status"
          value={states.statusFilter}
          onChange={handleStatusFilterChange}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="sent_for_approval">Sent For Approval</MenuItem>
          {/* <MenuItem value="approved">Approved</MenuItem> */}
        </Select>
      </FormControl>
    </Box>
  );

  const gridToolBar = () => (
    <Box>
      <CommonGridToolBarWithFilterText noExport searchHolder="Search" />
    </Box>
  );

  return (
    <Box sx={{ width: "94vw", height: "80vh", backgroundColor: Colors?.Background, padding: "0.5vw" }}>
      <Box
        className="SpecimenCollection-page"
        sx={{ width: "93vw", height: "78vh", backgroundColor: "white", display: "flex", flexDirection: "column", overflow: "hidden" }}
      >
        <FilterBar />
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => String(row.action_id ?? row.bill_id ?? row.patient_id ?? row.sno ?? "")}
          paginationModel={{ page: states.page, pageSize: states.pageSize }}
          onPaginationModelChange={(m) => setState((prev) => ({ ...prev, page: m.page, pageSize: m.pageSize }))}
          pageSizeOptions={[10, 20, 30]}
          pagination
          rowHeight={52}
          columnHeaderHeight={40}
          disableRowSelectionOnClick
          loading={states.loading}
          density="standard"
          showToolbar
          localeText={{
            toolbarColumns: "",
            toolbarDensity: "",
            toolbarExport: "",
            toolbarFilters: "",
            toolbarExportPrint: "",
          }}
          slots={{
            toolbar: gridToolBar,
            noRowsOverlay: () => (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                No Records To Be Shown
              </Box>
            ),
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 600, color: "#616161", fontSize: "0.9dvw" },
            "& .MuiDataGrid-cell": { fontSize: "0.9dvw" },
          }}
        />
      </Box>

      <ResultEnterPopup
        open={!!states.templatePopUpOpen}
        onClose={() =>
          setState((prev) => ({
            ...prev,
            templatePopUpOpen: false,
            selectedPatient: null,
            resultPopupData: null,
          }))
        }
        signatureData={states.signatureData || []}
        templateData={states.resultPopupData?.template_options ?? states.templateData ?? []}
        popupData={states.resultPopupData}
        popupLoading={states.resultPopupLoading}
        errorMessage={errorMessage}
        onSave={postResultTemplateData}
        selectedServiceData={states.selectedPatient}
        resultTextData={states.selectedPatient?.result_data}
        editorDisable={
          !canEditTab("Pending List") ||
          (states.resultPopupData
            ? (states.resultPopupData.status === "Completed" || states.resultPopupData.status === "Dispatched" ||
              (states.resultPopupData.status || "").toLowerCase() === "completed" ||
              (states.resultPopupData.status || "").toLowerCase() === "dispatched")
            : (states.selectedPatient?.result_status === "Completed" || states.selectedPatient?.result_status === "Dispatched"))
        }
        hideSaveAndPostButtons={
          (states.selectedPatient?.status === "sent_for_approval") ||
          (states.resultPopupData?.status && String(states.resultPopupData.status).toLowerCase() === "sent_for_approval")
        }
      />

      {states.isErrorMsg && <ToastMsg severity="error" msg={states.isErrorMsgText} msgPop={msgClose} />}
      {states.isSuccessMsg && <ToastMsg severity="success" msg={states.isSuccessMsgText} msgPop={msgClose} />}
    </Box>
  );
}
